import { Logout } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  CssBaseline,
  Divider,
  IconButton,
  List,
  ListItemIcon,
  Menu,
  MenuItem,
  styled,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { theme } from "../../../../styles";
import { ListItems } from "./ListItems";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import MuiDrawer from "@mui/material/Drawer";
import { useState } from "react";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { useNavigationService } from "../../../../../use-cases/navigation";
import { useAppSelector } from "../../../../hooks/store-hooks";
import { useAuthService } from "../../../../../use-cases/auth";
import { SessionTimer } from "../../../../components/session-timer/SessionTimer";

const drawerWidth: number = 240;

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  "& .MuiDrawer-paper": {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: "border-box",
    ...(!open && {
      overflowX: "hidden",
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      width: theme.spacing(7),
      [theme.breakpoints.up("sm")]: {
        width: theme.spacing(9),
      },
    }),
  },
}));

export const GeoVelesHeader = () => {
  // State
  const { currentUser } = useAppSelector((state) => state.user);
  // useState
  const [open, setOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  // Variables
  const openMenuAvatar = Boolean(anchorEl);
  // Service
  const { navigateTo } = useNavigationService();
  const { logout } = useAuthService();
  // Functions
  const toggleDrawer = () => {
    setOpen(!open);
  };
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <CssBaseline />
      <AppBar position="absolute" open={open}>
        <Toolbar
          sx={{
            pr: "24px", // keep right padding when drawer closed
          }}
        >
          <IconButton
            edge="start"
            color="inherit"
            aria-label="open drawer"
            onClick={toggleDrawer}
            sx={{
              marginRight: "36px",
              ...(open && { display: "none" }),
            }}
          >
            <MenuIcon />
          </IconButton>
          <div
            style={{
              flexGrow: 1,
              padding: `${theme.spacing(1)} 0`,
            }}
          >
            <Button
              onClick={() => navigateTo({ route: "/geo-veles" })}
              style={{
                display: "flex",
                cursor: "pointer",
                padding: theme.spacing(1),
                alignItems: "center",
                background: "transparent",
              }}
            >
              <div
                style={{
                  borderRadius: "50%",
                  filter: "brightness(0) invert(1)",
                  padding: "5px",
                  marginRight: "10px",
                }}
              >
                <img
                  src="/logo_geo_veles.png"
                  alt="Logo Geo Veles"
                  style={{
                    width: "40px",
                    height: "auto",
                  }}
                  onClick={() => navigateTo({ route: "/geo-veles" })}
                />
              </div>
              <Typography
                variant="h6"
                color={theme.palette.primary.contrastText}
                noWrap
                fontWeight={"bold"}
              >
                Veles-Geo
              </Typography>
            </Button>
          </div>
          <SessionTimer />
          <Tooltip title="Configurações">
            <IconButton
              onClick={handleClick}
              size="small"
              sx={{ ml: 2 }}
              aria-controls={openMenuAvatar ? "account-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={openMenuAvatar ? "true" : undefined}
            >
              <Avatar sx={{ width: 32, height: 32 }}>
                <Typography color="primary" fontWeight={"bold"}>
                  {currentUser?.Nome[0]}
                </Typography>
              </Avatar>
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={openMenuAvatar}
            onClose={handleClose}
            onClick={handleClose}
            slotProps={{
              paper: {
                elevation: 0,
                sx: {
                  overflow: "visible",
                  filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                  mt: 1.5,
                  "& .MuiAvatar-root": {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1,
                  },
                  "&::before": {
                    content: '""',
                    display: "block",
                    position: "absolute",
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: "background.paper",
                    transform: "translateY(-50%) rotate(45deg)",
                    zIndex: 0,
                  },
                },
              },
            }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            <MenuItem disabled sx={{ opacity: '0.95 !important', color: 'text.primary', fontWeight: 600, minWidth: 160 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                {currentUser?.Nome || "Usuário"}
              </Typography>
            </MenuItem>
            <Divider sx={{ my: 0.5 }} />
            <MenuItem
              onClick={() => {
                handleClose();
                logout();
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Logout fontSize="small" sx={{ color: 'error.main' }} />
                <Typography textAlign="center" color="error.main" sx={{ fontWeight: 500 }}>
                  Sair
                </Typography>
              </Box>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Drawer variant="permanent" open={open}>
        <Toolbar
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            px: [1],
          }}
        >
          <Button fullWidth onClick={() => {
            const loginUrl = String(process.env.REACT_APP_VELES_LOGIN_BASE_URL)
            const appBaseUrl = loginUrl.replace('/auth/login', '').replace('/authentication/login', '');
            window.localStorage.removeItem('LOGIN_TIMESTAMP');
            window.location.href = `${appBaseUrl}/modulos`;
          }}>
            <img
              src="/logo_veles.png"
              alt="Logo Veles"
              style={{ width: "100px" }}
            />
          </Button>
          <IconButton onClick={toggleDrawer}>
            <ChevronLeftIcon />
          </IconButton>
        </Toolbar>
        <Divider />
        <List
          component="nav"
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <ListItems isMenuOpen={open} />
        </List>
      </Drawer>
    </>
  );
};
